// no-dd-sa:typescript-best-practices/boolean-prop-naming
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { X, CalendarDays, CheckCircle2 } from 'lucide-react';
import { marketingApi } from '@/lib/api/marketing';
import type { MarketingLeadRequest } from '@/types/marketing';
import { logError } from '@/lib/logger';

type LeadCaptureModalProps = {
  open: boolean;
  onClose: () => void;
  onConnectNow: () => void;
};

const teamSizeOptions = ['50-99', '100-249', '250-499', '500+'];
const goalOptions = ['Cycle time & throughput', 'Board-ready reporting', 'Risk & compliance', 'Burnout & engagement insight'];

export default function LeadCaptureModal({ open, onClose, onConnectNow }: LeadCaptureModalProps) {
  const initialSource = useMemo(() => (typeof window !== 'undefined' ? window.location.href : ''), []);
  const [formData, setFormData] = useState<MarketingLeadRequest>({
    name: '',
    email: '',
    company: '',
    role: '',
    team_size: '',
    primary_goal: '',
    notes: '',
    source_url: initialSource,
    allow_contact: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const handleChange = (field: keyof MarketingLeadRequest, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await marketingApi.submitMarketingLead(formData);
      setStatus('success');
    } catch (err) {
      logError('Failed to submit lead form', err);
      setError('Something went wrong. Please try again or email hello@codepulsehq.com.');
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // overflow-y-auto is load-bearing: the form is taller than a phone
    // viewport, and a non-scrolling fixed overlay traps mobile users with
    // both the submit button and close X off-screen. Same structure as
    // PauseModal: scrolling container > backdrop layer > centered panel.
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            onClose();
          }
        }}
        role="button"
        tabIndex={-1}
        aria-label="Close modal"
      />
      <div className="flex min-h-full items-center justify-center px-4 py-6">
        <div
          className="relative w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl dark:bg-gray-900"
          role="dialog"
          aria-modal="true"
          aria-label="Executive briefing request"
        >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {status === 'success' ? (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-emerald-100 p-2 text-emerald-600">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">You&apos;re on the list!</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Grab a briefing slot or jump straight into the product—whatever fits your timeline.
                </p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <a
                href="https://cal.com/codepulse/briefing-request"
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center rounded-2xl border border-gray-200 p-5 text-center shadow-sm transition hover:border-primary-500 hover:shadow-md dark:border-gray-800"
              >
                <CalendarDays className="mb-2 h-6 w-6 text-primary-600" />
                <p className="font-semibold text-gray-900 dark:text-white">Book executive briefing</p>
                <p className="text-sm text-gray-500">Meet with a founder, align stakeholders, and cover security.</p>
              </a>
              <button
                type="button"
                onClick={onConnectNow}
                className="rounded-2xl border border-primary-200 bg-primary-50 p-5 text-center font-semibold text-primary-700 shadow-sm transition hover:bg-primary-100 dark:border-primary-500/40 dark:bg-primary-500/10 dark:text-primary-200"
              >
                Connect GitHub now
                <p className="mt-1 text-sm font-normal text-primary-600 dark:text-primary-300">Read-only scopes, SOC 2-aligned controls</p>
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-primary-600">Briefing Request</p>
              <h2 className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">Get a 1:1 executive walkthrough</h2>
              <p className="text-sm text-gray-500">
                Share a little context and we&apos;ll send the briefing agenda plus security docs. No SDRs or handoffs.
              </p>
              <a
                href="https://cal.com/codepulse/briefing-request"
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:underline dark:text-primary-400"
              >
                <CalendarDays className="h-4 w-4" />
                In a hurry? Skip the form and pick a time
              </a>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="lead-name" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <input
                  id="lead-name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="lead-email" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Work email
                </label>
                <input
                  id="lead-email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="lead-company" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Company
                </label>
                <input
                  id="lead-company"
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="lead-role" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Role / Title
                </label>
                <input
                  id="lead-role"
                  type="text"
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="lead-team-size" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Team size
                </label>
                <select
                  id="lead-team-size"
                  value={formData.team_size}
                  onChange={(e) => handleChange('team_size', e.target.value)}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                >
                  <option value="">Select</option>
                  {teamSizeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="lead-goal" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Primary initiative
                </label>
                <select
                  id="lead-goal"
                  value={formData.primary_goal}
                  onChange={(e) => handleChange('primary_goal', e.target.value)}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                >
                  <option value="">Select</option>
                  {goalOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="lead-notes" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Anything else we should prep?
              </label>
              <textarea
                id="lead-notes"
                rows={4}
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
              />
            </div>

            <div className="flex items-start gap-3 rounded-2xl bg-gray-50 p-4 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              <input
                id="lead-consent"
                type="checkbox"
                checked={formData.allow_contact}
                onChange={(e) => handleChange('allow_contact', e.target.checked)}
                className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="lead-consent">
                I&apos;m okay with follow-ups about this request. CodePulse never shares contact info with third parties.
              </label>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-6 py-3 font-semibold text-white shadow-lg shadow-primary-500/50 transition hover:bg-primary-500 disabled:opacity-60"
              >
                {isSubmitting ? 'Sending...' : 'Send briefing request'}
              </button>
              <a
                href="https://cal.com/codepulse/briefing-request"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-6 py-3 font-semibold text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Skip form → pick a time
              </a>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
}
